// Score storage manager for PPT Hero with different scoring rules per round type
// Integrates with Firebase database for user-specific scoring data
import { auth, db } from '../../../firebaseConfig.js';
import { getPlayerBaseline, getPlayerLevelBests } from '../../../services/analyticsService.js';

export default class ScoreStorageManager {
  constructor(scene) {
    this.scene = scene;
    this.baseStorageKey = 'pptHeroUnifiedScores';
    this.firebaseLoaded = false;
    this.scoreData = this.createDefaultScoreData();
    this.currentUserId = null;
    
    // Initialize with localStorage first, then load from Firebase
    this.loadScoreData();
  }

  // ENHANCED: Get user-specific storage key for data isolation
  getUserSpecificStorageKey() {
    const user = auth.currentUser;
    if (user && user.uid) {
      return `${this.baseStorageKey}_${user.uid}`;
    }
    return this.baseStorageKey; // Fallback for guest users
  }

  // ENHANCED: Load score data with user-specific isolation
  async loadScoreData() {
    try {
      const user = auth.currentUser;
      const userSpecificKey = this.getUserSpecificStorageKey();
      
      // CRITICAL: Check for user switching and clean contaminated data
      if (user && this.currentUserId && user.uid !== this.currentUserId) {
        console.log(`🔄 USER SWITCH in ScoreStorageManager: ${this.currentUserId} → ${user.uid}`);
        this.scoreData = this.createDefaultScoreData(); // Reset to clean state
      }
      
      this.currentUserId = user?.uid || null;
      
      // First load from user-specific localStorage for immediate UI
      const savedData = localStorage.getItem(userSpecificKey);
      if (savedData) {
        this.scoreData = JSON.parse(savedData);
        console.log(`📊 Loaded user-specific cached score data (${userSpecificKey}):`, Object.keys(this.scoreData));
      } else {
        this.scoreData = this.createDefaultScoreData();
        console.log('📊 No cached data found, using default score structure');
      }
      
      // Then load from Firebase if user is authenticated
      await this.loadFromFirebase();
      
    } catch (error) {
      console.error('Error loading score data:', error);
      this.scoreData = this.createDefaultScoreData();
    }
  }

  // Load user's scoring data from Firebase database
  async loadFromFirebase() {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log('📊 No authenticated user, using localStorage only');
        return;
      }

      console.log('📊 Loading PPT Hero scores from Firebase for user:', user.uid);

      // Load existing PPT Hero data from Firebase using existing services
      const [baselineData, levelBests, userScores] = await Promise.all([
        getPlayerBaseline(user.uid).catch(e => null),
        getPlayerLevelBests(user.uid, "PPTHero").catch(e => []),
        this.loadUserScoresFromFirebase(user.uid).catch(e => [])
      ]);

      // Process baseline data (calibration)
      if (baselineData) {
        this.scoreData.calibration.completed = true;
        this.scoreData.calibration.firstPlayScore = baselineData.totalScore || 0;
        this.scoreData.calibration.firstPlayStars = baselineData.starRating || 0;
        this.scoreData.calibration.bestScore = baselineData.totalScore || 0;
        this.scoreData.calibration.starRating = baselineData.starRating || 0;
        console.log('📊 Loaded calibration baseline from Firebase:', baselineData);
      }

      // Process level bests data
      if (levelBests && levelBests.length > 0) {
        this.processLevelBestsData(levelBests);
      }

      // Process user scores from scores collection
      if (userScores && userScores.length > 0) {
        this.processUserScoresData(userScores);
      }

      this.firebaseLoaded = true;
      this.saveScoreData(); // Cache the Firebase data locally
      console.log('📊 Successfully loaded scores from Firebase');

    } catch (error) {
      console.error('Error loading data from Firebase:', error);
    }
  }

  // Load user scores from Firebase scores collection
  async loadUserScoresFromFirebase(userId) {
    try {
      const scoresSnapshot = await db.collection('scores')
        .where('userUID', '==', userId)
        .where('game', '==', 'PPTHero')
        .orderBy('createdAt', 'desc')
        .get();

      const scores = [];
      scoresSnapshot.forEach(doc => {
        const data = doc.data();
        scores.push({
          ...data,
          id: doc.id
        });
      });

      return scores;
    } catch (error) {
      console.error('Error loading user scores from Firebase:', error);
      return [];
    }
  }

  // Process level bests data from Firebase
  processLevelBestsData(levelBests) {
    levelBests.forEach(levelData => {
      if (!levelData.levelNumber || !levelData.score) return;

      const level = levelData.levelNumber;
      const score = levelData.score;
      const stars = this.calculateStarRating(score, 1); // Single level

      // Map levels to rounds based on existing level structure
      if (level === 0) {
        // Practice - no scores stored
        return;
      } else if (level >= 1 && level <= 10) {
        // Levels 1-10 could be calibration or basic
        // If we have baseline data, these are likely additional basic round plays
        if (this.scoreData.calibration.completed) {
          // Update basic round if this is a better score
          if (score > this.scoreData.basic.bestScore) {
            this.scoreData.basic.bestScore = score;
            this.scoreData.basic.starRating = stars;
            this.scoreData.basic.completed = true;
          }
        }
      } else if (level >= 11 && level <= 20) {
        // Advanced round levels
        if (score > this.scoreData.advanced.bestScore) {
          this.scoreData.advanced.bestScore = score;
          this.scoreData.advanced.starRating = stars;
          this.scoreData.advanced.completed = true;
        }
      } else if (level >= 22 && level <= 30) {
        // Bain Basic round levels
        if (score > this.scoreData['bain-basic'].bestScore) {
          this.scoreData['bain-basic'].bestScore = score;
          this.scoreData['bain-basic'].starRating = stars;
          this.scoreData['bain-basic'].completed = true;
        }
      } else if (level >= 31 && level <= 40) {
        // Bain Advanced round levels
        if (score > this.scoreData['bain-advanced'].bestScore) {
          this.scoreData['bain-advanced'].bestScore = score;
          this.scoreData['bain-advanced'].starRating = stars;
          this.scoreData['bain-advanced'].completed = true;
        }
      }
    });

    console.log('📊 Processed level bests data from Firebase');
  }

  // Process user scores data from Firebase
  processUserScoresData(userScores) {
    // Group scores by round type and get best scores
    const roundScores = {
      calibration: [],
      basic: [],
      advanced: [],
      'bain-basic': [],
      'bain-advanced': []
    };

    userScores.forEach(scoreData => {
      // You might need to add metadata to scores to identify which round they're from
      // For now, we'll use the level information if available
      const level = scoreData.level || 0;
      const score = scoreData.score || 0;

      if (level >= 1 && level <= 10) {
        roundScores.basic.push(score);
      } else if (level >= 11 && level <= 20) {
        roundScores.advanced.push(score);
      }
      // Add more mapping as needed
    });

    // Update best scores for each round
    Object.keys(roundScores).forEach(roundId => {
      const scores = roundScores[roundId];
      if (scores.length > 0 && this.scoreData[roundId]) {
        const bestScore = Math.max(...scores);
        if (bestScore > this.scoreData[roundId].bestScore) {
          this.scoreData[roundId].bestScore = bestScore;
          this.scoreData[roundId].starRating = this.calculateStarRating(bestScore, 10);
          this.scoreData[roundId].completed = true;
        }
      }
    });

    console.log('📊 Processed user scores data from Firebase');
  }

  // Create default score data structure
  createDefaultScoreData() {
    return {
      // Practice round - no scores/stars stored
      practice: {
        hasScore: false,
        hasStars: false
      },
      
      // Calibration round - shared across generic and firm modes
      calibration: {
        hasScore: true,
        hasStars: true,
        bestScore: 0,
        starRating: 0,
        completed: false,
        firstPlayScore: 0, // Score when first completed - never changes
        firstPlayStars: 0,  // Stars when first completed - never changes
        sharedAcrossModes: true // Same data for generic and Bain modes
      },
      
      // Basic round - shared across generic and firm modes  
      basic: {
        hasScore: true,
        hasStars: true,
        bestScore: 0,
        starRating: 0,
        completed: false,
        sharedAcrossModes: true // Same data for generic and Bain modes
      },
      
      // Advanced round - shared across generic and firm modes, with feature flag
      advanced: {
        hasScore: true,
        hasStars: true,
        bestScore: 0,
        starRating: 0,
        completed: false,
        enabled: false, // Feature flag
        sharedAcrossModes: true // Same data for generic and Bain modes
      },
      
      // Bain Basic round - separate scoring for Bain users only
      'bain-basic': {
        hasScore: true,
        hasStars: true,
        bestScore: 0,
        starRating: 0,
        completed: false,
        firmSpecific: true
      },
      
      // Bain Advanced round - separate scoring for Bain users only, with feature flag
      'bain-advanced': {
        hasScore: true,
        hasStars: true,
        bestScore: 0,
        starRating: 0,
        completed: false,
        enabled: false, // Feature flag
        firmSpecific: true
      }
    };
  }

  // Save score data to localStorage
  saveScoreData() {
    try {
      const userSpecificKey = this.getUserSpecificStorageKey();
      localStorage.setItem(userSpecificKey, JSON.stringify(this.scoreData));
      console.log('📊 Saved unified score data:', this.scoreData);
    } catch (error) {
      console.error('Error saving score data:', error);
    }
  }

  // Update score for a specific round
  async updateRoundScore(roundId, score, starRating, completed = true) {
    if (!this.scoreData[roundId]) {
      console.warn(`Unknown round ID: ${roundId}`);
      return;
    }

    const roundData = this.scoreData[roundId];
    
    // Skip updates for rounds that don't track scores
    if (!roundData.hasScore) {
      console.log(`Round ${roundId} does not track scores - skipping update`);
      return;
    }

    let shouldSaveToFirebase = false;

    // Update best score if this is higher
    if (score > roundData.bestScore) {
      roundData.bestScore = score;
      shouldSaveToFirebase = true;
    }

    // Update star rating if this is higher
    if (starRating > roundData.starRating) {
      roundData.starRating = starRating;
      shouldSaveToFirebase = true;
    }

    // Mark as completed
    if (completed) {
      roundData.completed = true;
      shouldSaveToFirebase = true;
    }

    // Special handling for calibration - preserve first play score
    if (roundId === 'calibration' && !roundData.firstPlayScore && roundData.firstPlayScore !== 0) {
      roundData.firstPlayScore = score;
      roundData.firstPlayStars = starRating;
      shouldSaveToFirebase = true;
      console.log(`Calibration first play recorded: Score=${score}, Stars=${starRating}`);
    }

    // Save to localStorage immediately
    this.saveScoreData();
    
    // Save to Firebase if this is a meaningful update
    if (shouldSaveToFirebase) {
      await this.saveToFirebase(roundId, score, starRating);
    }
    
    console.log(`📊 Updated ${roundId} score: ${score}, stars: ${starRating}`);
  }

  // Save score data to Firebase using existing services
  async saveToFirebase(roundId, score, starRating) {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log('📊 No authenticated user, skipping Firebase save');
        return;
      }

      console.log(`📊 Saving ${roundId} score to Firebase: ${score} (${starRating} stars)`);

      // For calibration, save as baseline using existing service
      if (roundId === 'calibration') {
        const { savePlayerBaseline } = await import('../../../services/analyticsService.js');
        const baselineData = {
          userId: user.uid,
          completedAt: new Date().toISOString(),
          totalScore: score,
          starRating: starRating,
          levelsCompleted: 10, // Calibration has 10 levels
          totalLevels: 10,
          efficiency: 85, // Placeholder - could be calculated
          shortcutsUsed: {},
          gameMode: this.scene.scene?.key || 'PPTHero'
        };
        await savePlayerBaseline(user.uid, baselineData);
      }

      // Save to scores collection using existing service with BOTH field names for compatibility
      const { saveScoreToFirestore } = await import('../../../services/authService.js');
      const scoreData = {
        score: score,
        game: 'PPTHero',        // For Hall of Fame leaderboard queries
        gameType: 'PPTHero',    // For authService.js compatibility  
        player: user.displayName || user.email,
        userUID: user.uid,
        roundType: roundId,
        starRating: starRating,
        timestamp: Date.now(),
        createdAt: new Date(),  // For Firebase timestamp compatibility
        verified: user.emailVerified || false,
        // Additional Hall of Fame fields
        displayName: user.displayName || user.email,
        email: user.email
      };
      
      await saveScoreToFirestore(scoreData, true); // skipOverlay = true
      console.log(`📊 Successfully saved ${roundId} to Firebase`);
      
      // NEW: Record ranking snapshot for Hall of Fame trends
      if (window.HallOfFameRankingSystem) {
        try {
          await window.HallOfFameRankingSystem.recordPlayerRankingSnapshot(
            user.uid, 
            'PPTHero', 
            score
          );
          console.log(`📈 Ranking snapshot recorded for trends: ${user.uid}, PPTHero, ${score}`);
        } catch (rankingError) {
          console.warn('📈 Ranking snapshot failed (non-critical):', rankingError);
        }
      }
      
    } catch (error) {
      console.error('Error saving to Firebase:', error);
    }
  }

  // Get score data for a specific round
  getRoundScoreData(roundId) {
    return this.scoreData[roundId] || null;
  }

  // Get display data for round tiles based on round type and user context
  getRoundTileDisplayData(roundId, userContext = {}) {
    const roundData = this.scoreData[roundId];
    if (!roundData) {
      return { showScore: false, showStars: false, score: 0, stars: 0, enabled: true };
    }

    // Practice round - never show scores or stars
    if (roundId === 'practice') {
      return { 
        showScore: false, 
        showStars: false, 
        score: 0, 
        stars: 0, 
        enabled: true,
        greyedOut: false,
        greyColor: null
      };
    }

    // Check if round is enabled (for advanced rounds with feature flags)
    const enabled = roundData.enabled !== false; // Default to true if not specified
    
    // Determine grey color for disabled rounds
    let greyColor = null;
    let greyedOut = false;
    
    if (!enabled) {
      greyedOut = true;
      // Different grey color for disabled advanced vs practice
      if (roundId === 'advanced' || roundId === 'bain-advanced') {
        greyColor = 0x4a4a4a; // Darker grey for disabled advanced rounds
      }
    }

    // For calibration, show first-play data (consistent across modes)
    if (roundId === 'calibration') {
      return {
        showScore: roundData.hasScore && roundData.completed,
        showStars: roundData.hasStars && roundData.completed,
        score: roundData.firstPlayScore || 0,
        stars: roundData.firstPlayStars || 0,
        enabled: enabled,
        greyedOut: greyedOut,
        greyColor: greyColor
      };
    }

    // For other rounds, show best performance
    return {
      showScore: roundData.hasScore && roundData.completed,
      showStars: roundData.hasStars && roundData.completed, 
      score: roundData.bestScore || 0,
      stars: roundData.starRating || 0,
      enabled: enabled,
      greyedOut: greyedOut,
      greyColor: greyColor
    };
  }

  // Enable/disable feature flags for advanced rounds
  setAdvancedRoundEnabled(enabled) {
    this.scoreData.advanced.enabled = enabled;
    this.scoreData['bain-advanced'].enabled = enabled;
    this.saveScoreData();
    console.log(`📊 Advanced rounds ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Check if user has access to Bain-specific rounds
  hasBainAccess(userContext = {}) {
    return userContext.firmAccess && (
      userContext.firmAccess.includes('bain') ||
      userContext.firmAccess.includes('bain-basic') || 
      userContext.firmAccess.includes('bain-advanced') ||
      userContext.firm === 'bain'
    );
  }

  // Get all rounds with proper visibility based on user context
  getVisibleRounds(userContext = {}) {
    const allRounds = ['practice', 'calibration', 'basic', 'advanced'];
    
    // Add Bain rounds if user has access
    if (this.hasBainAccess(userContext)) {
      allRounds.push('bain-basic', 'bain-advanced');
    }
    
    return allRounds;
  }

  // Calculate star rating from score
  calculateStarRating(score, numLevels) {
    const averageScorePerLevel = score / numLevels;
    
    if (averageScorePerLevel >= 80) return 5;
    if (averageScorePerLevel >= 60) return 4; 
    if (averageScorePerLevel >= 40) return 3;
    if (averageScorePerLevel >= 25) return 2;
    if (averageScorePerLevel >= 10) return 1;
    return 0;
  }

  // Reset all scores (useful for testing)
  resetAllScores() {
    this.scoreData = this.createDefaultScoreData();
    this.saveScoreData();
    console.log('📊 Reset all scores');
  }

  // Export score data
  exportScoreData() {
    return {
      scoreData: this.scoreData,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
  }

  // Import score data
  importScoreData(data) {
    try {
      if (data.scoreData) {
        this.scoreData = data.scoreData;
        this.saveScoreData();
        console.log('📊 Imported score data:', data);
        return true;
      }
    } catch (error) {
      console.error('Error importing score data:', error);
    }
    return false;
  }
}
